import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Ban, BadgeCheck, IdCard, ShieldCheck, Snowflake, Tags, Trash2, Users } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  fetchUsers,
  banUser,
  unbanUser,
  freezeUser,
  unfreezeUser,
  verifyUser,
  labelUser,
  clearUserLabel,
  deleteUser,
  type AdminUserRow,
  type ModerationLabel,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, PIIField, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { SavedViews } from '@/components/SavedViews'
import { DangerDialog } from '@/components/DangerDialog'
import { LabelDialog } from '@/components/LabelDialog'
import { useToast } from '@/providers/UiProviders'

/**
 * A3.01 — список користувачів. GET /admin/users, розширений пошук/фільтри по колонках
 * (DataTable filter-панель), збережені фільтри (SavedViews, клієнтський стан в URL).
 * Бан/заморозка/верифікація — реальні дії через EventFlow command bus у AdminServer
 * (UserAggregate.Ban/Freeze/SetVerified), з реальним блокуванням входу/відправки
 * повідомлень на боці месенджера. Примусовий вихід/нотатки/страйки/ризик-скор —
 * не реалізовані, бекенд не має адмін-override для сесій.
 */
export function UsersScreen() {
  const t = useT()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [viewKey, setViewKey] = useState(0)
  const [reloadKey, setReloadKey] = useState(0)
  const [banTarget, setBanTarget] = useState<AdminUserRow | null>(null)
  const [freezeTarget, setFreezeTarget] = useState<AdminUserRow | null>(null)
  const [labelTarget, setLabelTarget] = useState<AdminUserRow | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null)

  const nameOf = (r: AdminUserRow) =>
    [r.first_name, r.last_name].filter(Boolean).join(' ') || (r.username ? `@${r.username}` : `#${r.id}`)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.users.actionDone, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.users.actionFailed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
      }
    },
    [toast, t],
  )

  const rowActions = useMemo<RowAction<AdminUserRow>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/users/${r.id}`),
      },
      {
        id: 'ban',
        label: t.users.ban,
        icon: <Ban size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => !r.is_banned,
        onSelect: (r) => setBanTarget(r),
      },
      {
        id: 'unban',
        label: t.users.unban,
        icon: <ShieldCheck size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.is_banned,
        onSelect: (r) => void run(() => unbanUser(r.id)),
      },
      {
        id: 'freeze',
        label: t.users.freeze,
        icon: <Snowflake size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => !r.is_frozen,
        onSelect: (r) => setFreezeTarget(r),
      },
      {
        id: 'unfreeze',
        label: t.users.unfreeze,
        icon: <ShieldCheck size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.is_frozen,
        onSelect: (r) => void run(() => unfreezeUser(r.id)),
      },
      {
        id: 'verify',
        label: t.users.verify,
        icon: <BadgeCheck size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.status !== 'banned',
        onSelect: (r) => void run(() => verifyUser(r.id, true)),
      },
      {
        id: 'label',
        label: t.users.label,
        icon: <Tags size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        onSelect: (r) => setLabelTarget(r),
      },
      {
        id: 'delete',
        label: t.users.deleteAccount,
        icon: <Trash2 size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.status !== 'deleted',
        onSelect: (r) => setDeleteTarget(r),
      },
    ],
    [t, run, navigate],
  )

  const columns = useMemo<AdminColumn<AdminUserRow>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 90 },
      {
        key: 'name',
        header: t.col.name,
        type: 'text',
        accessor: (r) =>
          [r.first_name, r.last_name].filter(Boolean).join(' ') || (r.username ? `@${r.username}` : ''),
        sortable: false,
        minWidth: 160,
      },
      { key: 'username', header: t.col.username, type: 'text', width: 150 },
      {
        key: 'phone',
        header: t.col.phone,
        type: 'pii',
        piiLevel: 'L2',
        width: 160,
        sortable: false,
        filterable: false,
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'active', label: t.status.active, status: 'active' },
          { value: 'banned', label: t.status.banned, status: 'banned' },
          { value: 'deleted', label: t.status.deleted, status: 'deleted' },
        ],
      },
      {
        key: 'is_frozen',
        header: t.users.freeze,
        type: 'bool',
        width: 90,
      },
      {
        key: 'label',
        header: t.users.label,
        type: 'text',
        width: 100,
        sortable: false,
      },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 140 },
      { key: 'last_seen', header: t.users.lastSeen, type: 'date', width: 140, hiddenByDefault: true },
    ],
    [t],
  )

  const fetcher = useCallback(fetchUsers, [])

  return (
    <>
      <PageHeader
        title={t.nav.users}
        screenId="A3.01"
        verified
        actions={
          <SavedViews
            storageKey="users"
            onApply={(search) => {
              window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
              setViewKey((k) => k + 1)
            }}
          />
        }
      />
      <DataTable<AdminUserRow>
        key={`${viewKey}-${reloadKey}`}
        screenId="A3.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        rowActions={rowActions}
        emptyState={{ icon: <Users size={28} strokeWidth={1.5} /> }}
        detailPanel={(r) => (
          <div>
            <InfoRow label={t.col.id}>
              <MonoValue value={String(r.id)} />
            </InfoRow>
            <InfoRow label={t.col.name}>
              {[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
            </InfoRow>
            <InfoRow label={t.col.username}>{r.username ? `@${r.username}` : '—'}</InfoRow>
            <InfoRow label={t.col.phone}>
              {r.phone ? <PIIField value={r.phone} level="L2" /> : '—'}
            </InfoRow>
            <InfoRow label={t.col.status}>
              <div className="flex items-center gap-1.5">
                <StatusBadge status={r.status === 'banned' ? 'banned' : 'active'} />
                {r.is_frozen && <Tag>{t.users.freeze}</Tag>}
                {r.label && <Tag>{r.label}</Tag>}
              </div>
            </InfoRow>
            <InfoRow label={t.col.registered}>
              <RelativeTime value={r.created_at} />
            </InfoRow>
          </div>
        )}
      />

      {banTarget && (
        <DangerDialog
          open
          onClose={() => setBanTarget(null)}
          screenId="A3.27"
          level={2}
          title={t.users.banTitle(nameOf(banTarget))}
          description={t.users.banDesc}
          confirmLabel={t.users.ban}
          onConfirm={async ({ reason }) => {
            try {
              await banUser(banTarget.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e // dialog stays open
            }
          }}
        />
      )}

      {freezeTarget && (
        <DangerDialog
          open
          onClose={() => setFreezeTarget(null)}
          screenId="A3.27"
          level={2}
          title={t.users.freezeTitle(nameOf(freezeTarget))}
          description={t.users.freezeDesc}
          confirmLabel={t.users.freeze}
          onConfirm={async ({ reason }) => {
            try {
              await freezeUser(freezeTarget.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}

      {labelTarget && (
        <LabelDialog
          open
          onClose={() => setLabelTarget(null)}
          title={t.users.labelTitle(nameOf(labelTarget))}
          currentLabel={labelTarget.label}
          onSelect={(label: ModerationLabel) => run(() => labelUser(labelTarget.id, label))}
          onClear={() => run(() => clearUserLabel(labelTarget.id))}
        />
      )}

      {deleteTarget && (
        <DangerDialog
          open
          onClose={() => setDeleteTarget(null)}
          screenId="A3.27"
          level={3}
          objectName={deleteTarget.username ? `@${deleteTarget.username}` : nameOf(deleteTarget)}
          title={t.users.deleteAccountTitle(nameOf(deleteTarget))}
          description={t.users.deleteAccountDesc}
          confirmLabel={t.users.deleteAccount}
          onConfirm={async () => {
            try {
              await deleteUser(deleteTarget.id)
              toast(t.users.actionDone, { tone: 'success' })
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
